import React, { useState, useEffect, useCallback, useRef } from "react";
import { Icon, Button } from "semantic-ui-react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchSettings, updateSettings } from "../settingsActions";
import { useSubscriptionStatus } from "../../../../../hooks/useSubscriptionStatus";
import SubscriptionRestriction from "../../../subscriptionRestriction/SubscriptionRestriction";
import "./General.css";

const PrivacyVisibility = ({
  settings,
  loading,
  error,
  fetchSettings,
  updateSettings,
}) => {
  const { t } = useTranslation();
  const { isSuspended } = useSubscriptionStatus();
  const history = useHistory();

  const [isSwitch1On, setIsSwitch1On] = useState(true);
  const [isSwitch2On, setIsSwitch2On] = useState(true);
  const [isSwitch3On, setIsSwitch3On] = useState(true);

  const debounceTimeouts = useRef({});
  const editingFields = useRef(new Set());

  const debouncedUpdateSettings = useCallback(
    (fieldName, value, delay = 500) => {
      if (debounceTimeouts.current[fieldName]) {
        clearTimeout(debounceTimeouts.current[fieldName]);
      }

      debounceTimeouts.current[fieldName] = setTimeout(async () => {
        try {
          await updateSettings(fieldName, value);
          delete debounceTimeouts.current[fieldName];
          editingFields.current.delete(fieldName);
          if (fieldName === "IsLeaveTypeHidden") {
            editingFields.current.delete("isSwitch1On");
          } else if (fieldName === "IsCalendarViewHidden") {
            editingFields.current.delete("isSwitch2On");
          } else if (fieldName === "CanViewOtherDepartments") {
            editingFields.current.delete("isSwitch3On");
          }
        } catch {
          delete debounceTimeouts.current[fieldName];
          editingFields.current.delete(fieldName);
          if (fieldName === "IsLeaveTypeHidden") {
            editingFields.current.delete("isSwitch1On");
          } else if (fieldName === "IsCalendarViewHidden") {
            editingFields.current.delete("isSwitch2On");
          } else if (fieldName === "CanViewOtherDepartments") {
            editingFields.current.delete("isSwitch3On");
          }
        }
      }, delay);
    },
    [updateSettings]
  );

  useEffect(() => {
    if (isSuspended) {
      history.replace("/settings/company?tab=billing");
    }
  }, [isSuspended, history]);

  useEffect(() => {
    if (!isSuspended) {
      fetchSettings();
    }
  }, [fetchSettings, isSuspended]);

  useEffect(() => {
    if (settings) {
      if (!editingFields.current.has("isSwitch1On")) {
        setIsSwitch1On(
          settings.isLeaveTypeHidden !== undefined ? !settings.isLeaveTypeHidden : true
        );
      }
      if (!editingFields.current.has("isSwitch2On")) {
        setIsSwitch2On(
          settings.isCalendarViewHidden !== undefined ? !settings.isCalendarViewHidden : true
        );
      }
      if (!editingFields.current.has("isSwitch3On")) {
        setIsSwitch3On(
          settings.canViewOtherDepartments !== undefined
            ? settings.canViewOtherDepartments
            : true
        );
      }
    }
  }, [settings]);

  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts.current).forEach((timeout) => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  if (isSuspended) {
    return (
      <div style={{ padding: "20px" }}>
        <SubscriptionRestriction actionKey="app.subscriptionRestriction.actionAccessSettings" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="workspace-settings">
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Icon name="spinner" loading size="large" />
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="workspace-settings">
        <div style={{ textAlign: "center", padding: "50px", color: "#e53e3e" }}>
          <Icon name="exclamation triangle" size="large" />
          <p>
            {t("settings.general.errorLoadingSettings")} {error}
          </p>
          <Button onClick={() => fetchSettings()}>{t("settings.general.retry")}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="workspace-settings">
      <div className="settings-section">
        <div className="section-header">
          <div className="section-icon privacy">
            <Icon name="lock" className="m-0 mb-1" />
          </div>
          <div className="section-content">
            <h3 className="section-title">{t("settings.general.privacyVisibility")}</h3>
            <p className="section-description">
              {t("settings.general.privacyVisibilityDescription")}
            </p>
          </div>
        </div>

        <div className="privacy-settings">
          <div className="privacy-card">
            <div className="privacy-content">
              <div className="privacy-info">
                <div className="privacy-header">
                  <span className="privacy-icon">
                    <Icon name="eye" className="m-0 mb-1" />
                  </span>
                  <h4 className="privacy-title">{t("settings.general.leaveTypesVisibility")}</h4>
                </div>
                <p className="privacy-description">
                  {t("settings.general.leaveTypesVisibilityDescription")}
                </p>
              </div>
              <div className="privacy-control">
                <div className="toggle-switch-container">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={isSwitch1On}
                      onChange={() => {
                        const nextVisible = !isSwitch1On;
                        setIsSwitch1On(nextVisible);
                        editingFields.current.add("isSwitch1On");
                        debouncedUpdateSettings("IsLeaveTypeHidden", !nextVisible, 300);
                      }}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className="toggle-label">
                    {isSwitch1On ? t("settings.general.visible") : t("settings.general.hidden")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="privacy-card">
            <div className="privacy-content">
              <div className="privacy-info">
                <div className="privacy-header">
                  <span className="privacy-icon">
                    <Icon name="calendar alternate" className="m-0 mb-1" />
                  </span>
                  <h4 className="privacy-title">{t("settings.general.calendarViewAccess")}</h4>
                </div>
                <p className="privacy-description">
                  {t("settings.general.calendarViewAccessDescription")}
                </p>
              </div>
              <div className="privacy-control">
                <div className="toggle-switch-container">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={isSwitch2On}
                      onChange={() => {
                        const nextVisible = !isSwitch2On;
                        setIsSwitch2On(nextVisible);
                        editingFields.current.add("isSwitch2On");
                        debouncedUpdateSettings("IsCalendarViewHidden", !nextVisible, 300);
                      }}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className="toggle-label">
                    {isSwitch2On ? t("settings.general.visible") : t("settings.general.hidden")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="privacy-card">
            <div className="privacy-content">
              <div className="privacy-info">
                <div className="privacy-header">
                  <span className="privacy-icon">
                    <Icon name="users" className="m-0 mb-1" />
                  </span>
                  <h4 className="privacy-title">{t("settings.general.otherDepartmentsAccess")}</h4>
                </div>
                <p className="privacy-description">
                  {t("settings.general.otherDepartmentsAccessDescription")}
                </p>
              </div>
              <div className="privacy-control">
                <div className="toggle-switch-container">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={isSwitch3On}
                      onChange={() => {
                        const next = !isSwitch3On;
                        setIsSwitch3On(next);
                        editingFields.current.add("isSwitch3On");
                        debouncedUpdateSettings("CanViewOtherDepartments", next, 300);
                      }}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className="toggle-label">
                    {isSwitch3On ? t("settings.general.visible") : t("settings.general.hidden")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  settings: state.settingsReducer,
  loading: state.settingsReducer.loading,
  error: state.settingsReducer.error,
});

const mapDispatchToProps = {
  fetchSettings,
  updateSettings,
};

export default connect(mapStateToProps, mapDispatchToProps)(PrivacyVisibility);
